import React, { FC } from 'react'
import styled from 'styled-components'
import { ModalProps, ModalView } from 'components/organisms/Modal'
import Button from 'components/molecules/Button'
import { useMutation, useQueryClient } from 'react-query'
import { Controller, useForm } from 'react-hook-form'
import InputWithCaptions from 'components/molecules/InputWithCaptions'
import { createFeatureGroupsFn } from 'api'
import { FeatureGroup } from '@types'
import { useTranslation } from 'react-i18next'
import { SwitchField } from '../components/SwitchField'
import { useUserContext } from 'contexts/UserContext'
import { Roles } from 'utils/consts'

const defaultConfig = {
  privacy: 1,
  multiple_identities: 0,
  multiple_devices: 0,
  pstn: 0,
  promo_credits: 5,
  ptt: 0,
  notarized_receipts: 0,
  hide_app: 0,
  ptt_default_speaker: 0,
  ptt_force_enable_from_contacts: 0,
  video_conf: 0,
  show_location_during_call: 0,
  auto_message_disappear: 0,
  call_recording: 0,
  cr_auto_activate: 0,
  cr_show_controls: 0,
  cr_save_mode: 0,
  local_contacts_only: 0,
  message_security_options: 1,
  max_daily_transfer: 3,
  min_leftover_balance: 5,
  max_transfer_amount: 50,
  registration_mode: 2,
  disable_p2p: 0,
  external_auth: 0,
  unrestricted_voip: 0,
  show_channels: 1,
  local_contacts_editing: 1,
  roster_contacts: 0,
  server_contacts: 0,
  external_contact_triggers: 1,
}

const AddFeatureGroup: FC<ModalProps> = ({ onClose }) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<FeatureGroup>({ defaultValues: defaultConfig })

  const { state } = useUserContext()
  const { i_realm } = state?.realm
  const isSuperAdmin = state.admin?.type === Roles.SuperAdmin

  const { mutate: createFeatureGroup } = useMutation(
    (data: Partial<FeatureGroup>) => createFeatureGroupsFn({ ...data, i_realm }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('feature-groups')
        onClose()
      },
    }
  )

  const onSubmit = (values: FeatureGroup) => {
    createFeatureGroup(values)
  }

  return (
    <ModalView title={t('modal.addFeatureGroup.title')} style={{ maxWidth: '750px' }} onClose={onClose}>
      <Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Field>
            <Controller
              name="name"
              defaultValue=""
              control={control}
              rules={{ required: true }}
              render={({ field: { ref, name, ...props } }) => (
                <InputWithCaptions type="text" caption={t('common.name')} {...props} />
              )}
            />
          </Field>
          <Grid>
            <SwitchField
              name="external_contact_triggers"
              title={t('featureGroup.externalContactTriggers')}
              control={control}
            />
            <SwitchField name="local_contacts_editing" title={t('featureGroup.contactsEditing')} control={control} />
            <SwitchField name="local_contacts_only" title={t('featureGroup.allowLocalContacts')} control={control} />
            <SwitchField
              name="roster_contacts"
              title={t('featureGroup.loadContactsFromServer')}
              control={control}
            />
            <SwitchField name="show_channels" title={t('featureGroup.showChannels')} control={control} />
            <SwitchField name="multiple_devices" title={t('featureGroup.multipleDevices')} control={control} />
            {isSuperAdmin && (
              <>
                <SwitchField name="external_auth" title={t('featureGroup.externalAuth')} control={control} />
                <SwitchField
                  name="auto_message_disappear"
                  title={t('featureGroup.autoMessageDisappear')}
                  control={control}
                />
                <SwitchField name="call_recording" title={t('featureGroup.callRecording')} control={control} />

                <SwitchField name="disable_p2p" title={t('featureGroup.disableP2p')} control={control} />
                <SwitchField name="hide_app" title={t('featureGroup.hideApp')} control={control} />
                <SwitchField
                  name="message_security_options"
                  title={t('featureGroup.messageSecurityOptions')}
                  control={control}
                />
                <SwitchField
                  name="multiple_identities"
                  title={t('featureGroup.multipleIdentities')}
                  control={control}
                />
                <SwitchField
                  name="notarized_receipts"
                  title={t('featureGroup.notarizedReceipts')}
                  control={control}
                />
                <SwitchField name="privacy" title={t('featureGroup.privacy')} control={control} />
                <SwitchField name="promo_credits" title={t('featureGroup.promoCredits')} control={control} />
                <SwitchField name="ptt" title={t('featureGroup.allowPushToTalk')} control={control} />
                <SwitchField
                  name="server_contacts"
                  title={t('featureGroup.allowServerContacts')}
                  control={control}
                />
                <SwitchField
                  name="show_location_during_call"
                  title={t('featureGroup.showLocationDuringCall')}
                  control={control}
                />
                <SwitchField name="unrestricted_voip" title={t('featureGroup.unrestrictedVoip')} control={control} />
                <SwitchField name="video_conf" title={t('featureGroup.videoConference')} control={control} />
                <SwitchField name="wallet" title={t('featureGroup.wallet')} control={control} />

                <SwitchField name="cr_auto_activate" title={t('featureGroup.crAutoActivate')} control={control} />
                <SwitchField name="cr_save_mode" title={t('featureGroup.crSaveMode')} control={control} />
                <SwitchField name="cr_show_controls" title={t('featureGroup.crShowControls')} control={control} />
                <SwitchField
                  name="ptt_default_speaker"
                  title={t('featureGroup.pttDefaultSpeaker')}
                  control={control}
                />
                <SwitchField
                  name="ptt_force_enable_from_contacts"
                  title={t('featureGroup.pttForceEnableFromContacts')}
                  control={control}
                />

                <Controller
                  name="force_jitsi_call_participants_amount"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions
                      type="number"
                      caption={t('featureGroup.forceJitsiCallParticipantsAmount')}
                      {...props}
                    />
                  )}
                />

                <Controller
                  name="contact_joined_notification_mode"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions
                      type="number"
                      caption={t('featureGroup.contactJoinedNotificationMode')}
                      {...props}
                    />
                  )}
                />

                <Controller
                  name="max_daily_transfer"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions type="number" caption={t('featureGroup.maxDailyTransfer')} {...props} />
                  )}
                />
                <Controller
                  name="max_transfer_amount"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions type="number" caption={t('featureGroup.maxTransferAmount')} {...props} />
                  )}
                />
                <Controller
                  name="min_leftover_balance"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { ref, name, ...props } }) => (
                    <InputWithCaptions type="number" caption={t('featureGroup.minLeftoverBalance')} {...props} />
                  )}
                />
              </>
            )}
          </Grid>

          <SubmitButton appearance="blue" type="submit" disabled={!isDirty || !isValid}>
            {t('modal.addFeatureGroup.addGroup')}
          </SubmitButton>
        </Form>
      </Content>
    </ModalView>
  )
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const Form = styled.form`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
`

const Grid = styled.div`
  display: grid;
  max-height: calc(100vh - 280px);
  grid-template-columns: repeat(auto-fill, 220px);
  grid-gap: 10px;
  width: 100%;
  overflow: auto;
`

const Field = styled.div`
  display: flex;
  margin-bottom: 24px;
  gap: 12px;
  & > * {
    width: 230px;
  }
`

const SubmitButton = styled(Button)`
  margin-top: 32px;
  align-self: flex-end;
`

export default AddFeatureGroup
